import React, { useEffect, useState, useRef } from 'react';
import { X, Search, Clock, RefreshCw, ScrollText, ChevronDown, Upload } from 'lucide-react';
import './MapTools.css';
import { useCurrentUser, useCurrentSpace } from '../../../context/authHooks';
import { useTeamMembers } from '../../settings/hooks/useTeam';
import { useToast } from '../../../shared/ui/Toast/ToastProvider';
import { useMapBridge } from '../../../shared/store/useMapBridge';

// Fase 5.18 (05-remediation.md, parte 2) -- `react-hooks/set-state-in-effect` (hint de React
// Compiler, no un bug de runtime) sube a `error` en todo `src/`. Este archivo hace setState
// síncrono dentro de un `useEffect` en un patrón de sincronización legítimo: hidrata el formulario de edición de asset cuando `useMapBridge` (el canal interno sancionado del mapa) señala un asset a editar.
// No se reescribe a estado derivado (repetiría el fetch/haría el UI menos predecible en
// varios de estos casos) -- deshabilitado a nivel de archivo, con motivo, no en silencio.
/* eslint-disable react-hooks/set-state-in-effect */
const UserSelect = ({ value, onChange, options, user }) => {
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef(null);

    const getPic = (u) => u?.picture || u?.image || u?.['link-profile-img'];
    const selectedOption = value === user?.id 
        ? { id: user?.id, name: `${user?.name} (Tú)`, picture: getPic(user) }
        : options.find(o => o.id === value) || { id: 'none', name: 'Sin asignar', picture: null };
    if (selectedOption.id !== 'none' && !selectedOption.picture) selectedOption.picture = getPic(selectedOption);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    return (
        <div className="custom-select-container" ref={containerRef}>
            <div className="custom-select-button" onClick={() => setIsOpen(!isOpen)}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                    {selectedOption.picture ? (
                        <img src={selectedOption.picture} alt="" style={{ width: '20px', height: '20px', borderRadius: '50%', border: '1px solid #333' }} />
                    ) : (
                        <div style={{ width: '20px', height: '20px', borderRadius: '50%', background: '#333' }}></div>
                    )}
                    <span>{selectedOption.name}</span>
                </div>
                <ChevronDown size={14} color="#888" />
            </div>
            {isOpen && (
                <div className="custom-select-dropdown">
                    <div className="custom-select-option" onClick={() => { onChange(user?.id); setIsOpen(false); }}>
                        <img src={getPic(user) || 'https://via.placeholder.com/20'} alt="" style={{ width: '20px', height: '20px', borderRadius: '50%', objectFit: 'cover' }} />
                        <span style={{ fontSize: '13px', color: '#ccc' }}>{user?.name} (Tú)</span>
                    </div>
                    {options.map(opt => {
                        const optPic = getPic(opt) || 'https://via.placeholder.com/20';
                        return (
                        <div key={opt.id} className="custom-select-option" onClick={() => { onChange(opt.id); setIsOpen(false); }}>
                            <img src={optPic} alt="" style={{ width: '20px', height: '20px', borderRadius: '50%', objectFit: 'cover' }} />
                            <span style={{ fontSize: '13px', color: '#ccc' }}>{opt.name}</span>
                        </div>
                        );
                    })}
                    <div className="custom-select-option" onClick={() => { onChange('none'); setIsOpen(false); }}>
                        <div style={{ width: '20px', height: '20px', borderRadius: '50%', background: '#333' }}></div>
                        <span style={{ fontSize: '13px', color: '#ccc' }}>Sin asignar</span>
                    </div>
                </div>
            )}
        </div>
    );
};

const MinimalLineChart = () => {
    // 7 days mock data
    const data = [20, 50, 40, 80, 60, 90, 75];
    const max = 100;
    const width = 280;
    const height = 40;
    const padding = 5;

    const points = data.map((val, idx) => {
        const x = (idx / (data.length - 1)) * (width - padding * 2) + padding;
        const y = height - padding - (val / max) * (height - padding * 2);
        return `${x},${y}`;
    }).join(' ');

    return (
        <div style={{ marginTop: 'var(--space-4)', width: '100%' }}>
            <div style={{ fontSize: '11px', color: '#888', marginBottom: 'var(--space-1)' }}>Actividad (Últimos 7 días)</div>
            <svg width="100%" height={height} viewBox={`0 0 ${width} ${height}`} style={{ overflow: 'visible' }}>
                <polyline 
                    fill="none" 
                    stroke="#a855f7" 
                    strokeWidth="1.5" 
                    points={points} 
                />
                {data.map((val, idx) => {
                    const x = (idx / (data.length - 1)) * (width - padding * 2) + padding;
                    const y = height - padding - (val / max) * (height - padding * 2);
                    return (
                        <circle key={idx} cx={x} cy={y} r="3" fill="#1e1e1e" stroke="#a855f7" strokeWidth="1.5" />
                    );
                })}
            </svg>
        </div>
    );
};

const SettingsAssets = () => {
    const [assetData, setAssetData] = useState(null);
    const [isOpen, setIsOpen] = useState(false);
    const [activeTab, setActiveTab] = useState('General');
    const [assignedUser, setAssignedUser] = useState('none');
    const [terminalName, setTerminalName] = useState('');
    
    // 03.1-auth-store.md §6 grupo 2 -- `user` acá solo se usa como identidad (id/name/image)
    // via `UserSelect`, y `currentSpace.id` para el fetch de miembros -- ambos dentro del
    // alcance angosto de `useCurrentUser()`/`useCurrentSpace()`.
    const user = useCurrentUser();
    const { currentSpace } = useCurrentSpace();
    const { data: teamMembers = [] } = useTeamMembers(currentSpace?.id);
    const toast = useToast();

    // edit-map-asset (paso a3): antes un CustomEvent de `window`, ahora un selector de
    // `useMapBridge` -- Map llama `openAssetEditor(asset)`, este efecto reacciona cada vez que
    // `editingAsset` cambia.
    const editingAsset = useMapBridge((s) => s.editingAsset);
    useEffect(() => {
        if (editingAsset) {
            setAssetData(editingAsset);
            setTerminalName(editingAsset.name || '');
            setAssignedUser(editingAsset.owner_id || 'none');
            setIsOpen(true);
            if (window.location.pathname.endsWith('/maptools')) {
                window.history.pushState({}, '', `${window.location.pathname}/${editingAsset.id}`);
            }
        }
    }, [editingAsset]);

    if (!isOpen || !assetData) return null;

    const tabs = ['General', 'Conexiones', 'Subrutinas', 'Logs', 'Actividad'];

    const handleNameChange = (e) => {
        setTerminalName(e.target.value);
    };

    const handleSaveName = () => {
        useMapBridge.getState().requestFieldUpdate({ id: assetData.id, field: 'name', value: terminalName });
        toast.success('Nombre de terminal guardado');
    };

    const handleSaveUser = (userId) => {
        setAssignedUser(userId);
        const owner_id = userId === 'none' ? null : userId;
        useMapBridge.getState().requestFieldUpdate({ id: assetData.id, field: 'owner_id', value: owner_id });
        toast.success('Usuario asignado guardado');
    };

    return (
        <div className="maptools-panel-container" style={{ left: 'calc(59px + 250px)', background: '#181818', width: '500px' }}>
            <div className="maptools-panel-header" style={{ padding: 'var(--space-4)', background: '#222', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
                    <h3 style={{ margin: 0 }}>{terminalName || 'Terminal'}</h3>
                    <span style={{ background: '#000', color: '#888', padding: 'var(--space-1) var(--space-2)', borderRadius: 'var(--radius-sm)', fontSize: '11px', border: '1px solid #333' }}>
                        {assetData.id || 'sin id'}
                    </span>
                </div>
                <button className="maptools-close-btn" onClick={() => {
                    setIsOpen(false);
                    useMapBridge.getState().closeAssetEditor();
                    const parts = window.location.pathname.split('/');
                    if (parts.length > 3) {
                        window.history.pushState({}, '', parts.slice(0, -1).join('/'));
                    }
                }} title="Cerrar" style={{ background: 'transparent', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 'var(--space-1)' }}>
                    <X size={20} color="#FFF" />
                </button>
            </div>

            <div className="settings-assets-tabs">
                {tabs.map(tab => (
                    <div 
                        key={tab} 
                        className={`settings-assets-tab ${activeTab === tab ? 'active' : ''}`}
                        onClick={() => setActiveTab(tab)}
                    >
                        {tab}
                    </div>
                ))}
            </div>

            <div className="settings-assets-content" style={{ flexDirection: 'column', gap: 'var(--space-4)' }}>
                {activeTab === 'General' && (
                    <>
                        {/* TOP BOX (100% width) */}
                        <div className="settings-info-general">
                            {/* LEFT COLUMN */}
                            <div className="settings-info-left">
                                <div style={{ display: 'flex', gap: 'var(--space-4)' }}>
                                    {(() => {
                                        const selectedUserOpt = teamMembers?.find(m => m.id === assignedUser) || (user?.id === assignedUser ? user : null);
                                        const ownerPicture = selectedUserOpt?.picture || selectedUserOpt?.image || selectedUserOpt?.['link-profile-img'];
                                        return ownerPicture ? (
                                            <img 
                                                src={ownerPicture} 
                                                alt="Owner" 
                                                style={{ width: '64px', height: '64px', borderRadius: '50%', border: '2px solid #333', objectFit: 'cover' }}
                                            />
                                        ) : (
                                            <div title="Subir foto del sistema" style={{ width: '64px', height: '64px', borderRadius: '50%', border: '2px dashed #333', background: '#111', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                                                <Upload size={24} color="#666" />
                                            </div>
                                        );
                                    })()}
                                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                                        <div>
                                            <label style={{ fontSize: '11px', color: '#888' }}>Nombre de la Terminal</label>
                                            <input 
                                                type="text" 
                                                placeholder="coloca nombre a la terminal"
                                                value={terminalName} 
                                                onChange={handleNameChange}
                                                onBlur={handleSaveName}
                                                style={{ width: '100%', background: '#111', border: '1px solid #333', color: '#fff', padding: 'var(--space-1) var(--space-2)', borderRadius: 'var(--radius-sm)', fontSize: '13px' }}
                                            />
                                        </div>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <label style={{ fontSize: '11px', color: '#888' }}>ID Único</label>
                                            <span style={{ fontSize: '12px', color: assetData.id ? '#a855f7' : '#888' }}>
                                                {assetData.id ? `${assetData.id.substring(0, 8)}...` : 'sin id asignado'}
                                            </span>
                                        </div>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <label style={{ fontSize: '11px', color: '#888' }}>Estado</label>
                                            <span style={{ fontSize: '11px', color: '#39FF14', background: 'rgba(57, 255, 20, 0.1)', padding: 'var(--space-1) var(--space-2)', borderRadius: 'var(--radius-sm)' }}>Online</span>
                                        </div>
                                    </div>
                                </div>
                                <div style={{ marginTop: 'var(--space-4)' }}>
                                    <label style={{ fontSize: '11px', color: '#888' }}>Usuario asignado</label>
                                    <UserSelect 
                                        value={assignedUser} 
                                        onChange={handleSaveUser} 
                                        options={teamMembers || []} 
                                        user={user} 
                                    />
                                </div>
                            </div>

                            <div className="vertical-divider"></div>

                            {/* RIGHT COLUMN */}
                            <div className="settings-info-right">
                                {/* Estado Actual (Big Box) */}
                                <div className="settings-box" style={{ flex: 1.2, padding: 'var(--space-3)' }}>
                                    <h5 style={{ margin: '0 0 var(--space-3) 0', color: '#fff', fontSize: '14px' }}>Estado actual</h5>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-4)' }}>
                                        <div className="donut-chart" style={{ width: '50px', height: '50px' }}>
                                            <span className="donut-chart-text" style={{ fontSize: '12px' }}>35%</span>
                                        </div>
                                        <div>
                                            <p style={{ margin: '0 0 var(--space-1) 0', fontSize: '12px', color: '#ddd' }}>CPU: <span style={{ color: '#888' }}>18%</span></p>
                                            <p style={{ margin: '0', fontSize: '12px', color: '#ddd' }}>RAM: <span style={{ color: '#888' }}>45%</span></p>
                                        </div>
                                    </div>
                                    <MinimalLineChart />
                                </div>

                                {/* Stacked Boxes */}
                                <div style={{ flex: 0.8, display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
                                    <div className="settings-box" style={{ flex: 1, padding: 'var(--space-3)', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                                        <h5 style={{ margin: '0 0 var(--space-1) 0', color: '#aaa', fontSize: '12px' }}>Conexiones activas</h5>
                                        <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#3b82f6' }}>12</div>
                                        <button style={{ background: '#333', border: 'none', color: '#fff', padding: 'var(--space-1) var(--space-2)', borderRadius: 'var(--radius-sm)', cursor: 'pointer', fontSize: '11px', alignSelf: 'flex-start' }}>Ver conexiones activas</button>
                                    </div>
                                    <div className="settings-box" style={{ flex: 1, padding: 'var(--space-3)', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                                        <h5 style={{ margin: '0 0 var(--space-1) 0', color: '#aaa', fontSize: '12px' }}>Tareas activas</h5>
                                        <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#39FF14' }}>8</div>
                                        <button style={{ background: '#333', border: 'none', color: '#fff', padding: 'var(--space-1) var(--space-2)', borderRadius: 'var(--radius-sm)', cursor: 'pointer', fontSize: '11px', alignSelf: 'flex-start' }}>Ver tareas</button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* MIDDLE BOX (100% width, 3 cols) */}
                        <div className="middle-box">
                            <div className="middle-box-col">
                                <div className="blue-icon-box">
                                    <Clock size={16} />
                                </div>
                                <div>
                                    <div style={{ fontSize: '10px', color: '#888' }}>CREADO EN</div>
                                    <div style={{ fontSize: '12px', color: '#ddd' }}>24 Ago 2026</div>
                                </div>
                            </div>
                            
                            <div className="vertical-divider-partial"></div>

                            <div className="middle-box-col">
                                <div className="blue-icon-box">
                                    <RefreshCw size={16} />
                                </div>
                                <div>
                                    <div style={{ fontSize: '10px', color: '#888' }}>ÚLTIMA ACTUALIZACIÓN</div>
                                    <div style={{ fontSize: '12px', color: '#ddd' }}>Hoy a las 9:00 am</div>
                                </div>
                            </div>

                            <div className="vertical-divider-partial"></div>

                            <div className="middle-box-col">
                                <div className="blue-icon-box">
                                    <ScrollText size={16} />
                                </div>
                                <div>
                                    <div style={{ fontSize: '10px', color: '#888' }}>LOGS</div>
                                    <div style={{ fontSize: '12px', color: '#ddd' }}>1,432 registrados</div>
                                </div>
                            </div>
                        </div>

                        {/* BOTTOM BOX (Subrutinas) */}
                        <div className="settings-box" style={{ width: '100%', marginTop: 'var(--space-4)' }}>
                            <h5 style={{ margin: '0 0 var(--space-4) 0', color: '#fff', fontSize: '15px' }}>Subrutinas</h5>
                            <table className="settings-table-container">
                                <thead>
                                    <tr>
                                        <th>Nombre</th>
                                        <th>Descripción</th>
                                        <th>Frecuencia</th>
                                        <th>Estado</th>
                                        <th>Última ejecución</th>
                                        <th>Duración promedio</th>
                                    </tr>
                                </thead>
                                <tbody style={{ fontSize: '12px' }}>
                                    <tr>
                                        <td>Sincronización de datos</td>
                                        <td style={{ color: '#888' }}>Sync de DB principal</td>
                                        <td>1 hr</td>
                                        <td style={{ color: '#39FF14' }}>Activo</td>
                                        <td>12:05 PM</td>
                                        <td style={{ color: '#888' }}>45s</td>
                                    </tr>
                                    <tr>
                                        <td>Limpieza de doc</td>
                                        <td style={{ color: '#888' }}>Purgado de archivos temporales</td>
                                        <td>24 hrs</td>
                                        <td style={{ color: '#39FF14' }}>Activo</td>
                                        <td>Ayer</td>
                                        <td style={{ color: '#888' }}>2m 10s</td>
                                    </tr>
                                    <tr>
                                        <td>Reporte de métricas</td>
                                        <td style={{ color: '#888' }}>Genera logs de uso diario</td>
                                        <td>12 hrs</td>
                                        <td style={{ color: '#f59e0b' }}>Pausado</td>
                                        <td>Hace 2 días</td>
                                        <td style={{ color: '#888' }}>1m 30s</td>
                                    </tr>
                                    <tr>
                                        <td>Checkeo de tareas</td>
                                        <td style={{ color: '#888' }}>Validación de queues</td>
                                        <td>5 min</td>
                                        <td style={{ color: '#39FF14' }}>Activo</td>
                                        <td>Hace 2 min</td>
                                        <td style={{ color: '#888' }}>5s</td>
                                    </tr>
                                    <tr>
                                        <td>Respaldo</td>
                                        <td style={{ color: '#888' }}>Backup a GCS</td>
                                        <td>24 hrs</td>
                                        <td style={{ color: '#39FF14' }}>Activo</td>
                                        <td>03:00 AM</td>
                                        <td style={{ color: '#888' }}>15m 00s</td>
                                    </tr>
                                </tbody>
                            </table>
                            <div style={{ textAlign: 'center', margin: 'var(--space-6) 0 var(--space-2) 0' }}>
                                <button style={{ background: '#a855f7', border: 'none', color: '#fff', padding: 'var(--space-2) var(--space-4)', borderRadius: 'var(--radius-xl)', cursor: 'pointer', fontSize: '13px', fontWeight: 'bold' }}>
                                    Ver todas las subrutinas
                                </button>
                            </div>
                        </div>
                    </>
                )}
                {activeTab !== 'General' && (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%', color: '#666' }}>
                        Contenido de {activeTab} (Próximamente)
                    </div>
                )}
            </div>
        </div>
    );
};

export default SettingsAssets;
